import { useEffect, useRef, useState } from 'react';

/**
 * Tiny wrapper around the browser SpeechRecognition API.
 *
 *   const { supported, listening, start, stop } = useDictation((finalText) => {
 *     setBody((b) => (b ? b + ' ' : '') + finalText);
 *   });
 *
 * `supported` is false on browsers without the API (Firefox desktop, some
 * Android WebViews) — UI should hide the mic button in that case.
 */
export function useDictation(onResult) {
  const cbRef = useRef(onResult);
  useEffect(() => {
    cbRef.current = onResult;
  }, [onResult]);

  const SR =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!SR;

  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    if (!SR) return;
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = 'en-US';

    r.onresult = (e) => {
      let chunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          chunk += e.results[i][0].transcript;
        }
      }
      const trimmed = chunk.trim();
      if (trimmed && cbRef.current) cbRef.current(trimmed);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);

    recRef.current = r;
    return () => {
      try {
        r.abort();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = () => {
    const r = recRef.current;
    if (!r || listening) return;
    try {
      r.start();
      setListening(true);
    } catch {
      /* already started — ignore */
    }
  };

  const stop = () => {
    const r = recRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch {
      /* noop */
    }
    setListening(false);
  };

  return { supported, listening, start, stop };
}
